/**
 * Platform-specific installation guides
 * Single source of truth for onboarding install instructions (UnifiedOnboarding,
 * OnboardingGate, WorkspaceOnboardingWizard, IntegrationsGrid).
 *
 * docsUrl maps to https://docs.humblytics.com/how-to-get-started/<slug>
 * Beehiiv + Kit use the generic getting-started page until dedicated docs ship.
 */

import type { PlatformId, PlatformMedia } from "@/lib/types/platform"

export type PlatformGuideStep = {
  title: string
  description: string
}

export type PlatformGuide = {
  id: PlatformId
  name: string
  logo?: string
  steps: PlatformGuideStep[]
  /** Flat steps for email / compact UI */
  shortSteps: string[]
  docsUrl: string
  /** Optional walkthrough media shown in InstallScriptPanel */
  media?: PlatformMedia
  setupTime?: string
}

const DOCS = "https://docs.humblytics.com/how-to-get-started"

/**
 * Media notes:
 * - Prefer short Cloudflare Stream / Loom / YouTube walkthroughs when available.
 * - Canonical player frame is 16:9 (capped ~168px tall). Re-encode clips to 16:9
 *   before upload so Stream doesn't letterbox inside the frame.
 * - GIF/MP4 URLs can be swapped in without UI changes.
 * - Animated step preview always renders; `media` adds an optional watch toggle.
 */
const GENERIC_GUIDE: PlatformGuide = {
  id: "generic",
  name: "Other / Manual",
  setupTime: "~2 min",
  steps: [
    {
      title: "Copy the tracking code",
      description: "Use the Copy code button on your tracking snippet",
    },
    {
      title: "Open your site's head editor",
      description:
        "Most builders have a Custom Code, Header Scripts, or theme header.php setting",
    },
    {
      title: "Paste before </head>",
      description: "Paste the script in the <head> of every page you want to track",
    },
    {
      title: "Publish and verify",
      description: "Publish/deploy, then click Verify below",
    },
  ],
  shortSteps: [
    "Open your site's HTML or CMS theme editor",
    "Find the <head> tag near the top of every page",
    "Paste the script right before </head> and publish",
  ],
  docsUrl: DOCS,
}

/** High-traffic builders shown first in the install picker. */
/**
 * The six builders that cover most of the base. Shown as a single row in the
 * install picker so the step fits one viewport; everything else sits behind
 * "More platforms". Detection usually resolves before a user ever opens this,
 * and a detected platform outside this list is added to the row automatically.
 */
export const TOP_SELECTABLE_PLATFORMS: PlatformId[] = [
  "wordpress",
  "webflow",
  "framer",
  "shopify",
  "wix",
  "squarespace",
]

export const PRIMARY_SELECTABLE_PLATFORMS: PlatformId[] = [
  "wordpress",
  "webflow",
  "framer",
  "shopify",
  "wix",
  "squarespace",
  "nextjs",
  "bubble",
  "ghost",
  "kajabi",
  "beehiiv",
  "kit",
  "lovable",
  "podia",
]

/** Additional platforms / frameworks — revealed via “Show more”. */
export const MORE_SELECTABLE_PLATFORMS: PlatformId[] = [
  "bolt",
  "astro",
  "vuejs",
  "replit",
  "django",
  "laravel",
  "reactrouter",
  "v0",
]

/** Named platforms shown in pickers / grids (excludes generic). */
export const platformGuides: PlatformGuide[] = [
  {
    id: "wordpress",
    name: "WordPress",
    setupTime: "~3 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Install a header plugin",
        description:
          "In WordPress, go to Plugins → Add New and install 'WPCode' (Insert Headers and Footers)",
      },
      {
        title: "Paste in Header",
        description: "Go to Code Snippets → Header & Footer and paste the script in the Header section",
      },
      {
        title: "Save and publish",
        description: "Click Save changes, clear any cache, then verify below",
      },
    ],
    shortSteps: [
      "Install the 'WPCode' (Insert Headers and Footers) plugin",
      "Go to Code Snippets → Header & Footer",
      "Paste the script in the Header section and save",
    ],
    docsUrl: `${DOCS}/wordpress`,
  },
  {
    id: "webflow",
    name: "Webflow",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Site settings",
        description: "In Webflow, click the W logo → Site settings",
      },
      {
        title: "Paste in Head Code",
        description: "Open Custom Code and paste the script into the Head Code section",
      },
      {
        title: "Save and publish",
        description: "Save, then Publish to your custom or staging domain before verifying",
      },
    ],
    shortSteps: [
      "Go to Site settings → Custom Code",
      "Paste the script into the Head Code section",
      "Save and publish your site",
    ],
    docsUrl: `${DOCS}/webflow`,
    media: {
      type: "cloudflare-stream",
      id: "a62f941f0e69959a6541fcbc18419da3",
      customer: "customer-eza7mbqyvfgw2vdk",
      aspectRatio: "16 / 9",
      title: "How to add Humblytics to Webflow",
    },
  },
  {
    id: "framer",
    name: "Framer",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Site settings",
        description: "Click your site name in the top bar, then select Site settings → General",
      },
      {
        title: "Add Custom Code",
        description: "Scroll to Custom Code and paste into Start of <head>",
      },
      {
        title: "Publish",
        description: "Publish your site, then click Verify below",
      },
    ],
    shortSteps: [
      "Open your Framer project → Site settings → General",
      "Paste the script into Custom Code → Start of <head>",
      "Publish your site",
    ],
    docsUrl: `${DOCS}/framer`,
    media: {
      type: "cloudflare-stream",
      id: "7150c6514678593d850cfd32000b8df4",
      customer: "customer-eza7mbqyvfgw2vdk",
      aspectRatio: "16 / 9",
      title: "How to add Humblytics to Framer",
    },
  },
  {
    id: "wix",
    name: "Wix",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Custom Code",
        description: "In Wix, go to Settings → Custom Code → Add Custom Code",
      },
      {
        title: "Paste in Head",
        description: "Paste the script, set placement to Head, and apply to All Pages",
      },
      {
        title: "Save and verify",
        description: "Save, then verify installation below",
      },
    ],
    shortSteps: [
      "Go to Settings → Custom Code → Add Custom Code",
      "Paste the script and set placement to Head",
      "Apply to All Pages and save",
    ],
    docsUrl: `${DOCS}/wix`,
  },
  {
    id: "squarespace",
    name: "Squarespace",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Code Injection",
        description: "Go to Settings → Developer Tools → Code Injection",
      },
      {
        title: "Add to Header",
        description: "Paste the script in the Header section",
      },
      {
        title: "Save and verify",
        description: "Click Save, then verify below",
      },
    ],
    shortSteps: [
      "Go to Settings → Advanced → Code Injection",
      "Paste the script in the Header section",
      "Save changes",
    ],
    docsUrl: `${DOCS}/squarespace`,
  },
  {
    id: "shopify",
    name: "Shopify",
    setupTime: "~3 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Edit theme code",
        description: "In Shopify admin, go to Online Store → Themes → … → Edit code",
      },
      {
        title: "Paste in theme.liquid",
        description: "Open layout/theme.liquid and paste the script just before </head>",
      },
      {
        title: "Save and verify",
        description: "Save the file, then verify below",
      },
    ],
    shortSteps: [
      "Go to Online Store → Themes → Edit code",
      "Open theme.liquid and find the <head> tag",
      "Paste the script before </head> and save",
    ],
    docsUrl: `${DOCS}/shopify`,
  },
  {
    id: "nextjs",
    name: "Next.js",
    setupTime: "~5 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Grab the script URL / id from your tracking snippet",
      },
      {
        title: "Add to the root layout",
        description: "Open app/layout.tsx (App Router) or pages/_document.tsx (Pages Router)",
      },
      {
        title: "Use next/script",
        description:
          "Import Script from 'next/script' and render it with strategy='beforeInteractive'",
      },
      {
        title: "Deploy and verify",
        description: "Deploy your changes, then verify below",
      },
    ],
    shortSteps: [
      "Open app/layout.tsx (or pages/_document.tsx)",
      "Add <Script> from 'next/script' with strategy='beforeInteractive'",
      "Deploy your changes",
    ],
    docsUrl: `${DOCS}/nextjs`,
  },
  {
    id: "bubble",
    name: "Bubble",
    setupTime: "~3 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open SEO / metatags",
        description: "In the Bubble editor, go to Settings → SEO / metatags",
      },
      {
        title: "Paste in header scripts",
        description: "Paste into Script/meta tags in header so it loads on every page",
      },
      {
        title: "Deploy to live",
        description: "Click Deploy → Deploy to live (not version-test), then verify",
      },
    ],
    shortSteps: [
      "Settings → SEO / metatags → Script/meta tags in header",
      "Paste the Humblytics script",
      "Deploy to live (not /version-test), then verify",
    ],
    docsUrl: `${DOCS}/bubble`,
  },
  {
    id: "ghost",
    name: "Ghost",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Code Injection",
        description: "In Ghost Admin, go to Settings → Code Injection",
      },
      {
        title: "Paste in Site Header",
        description: "Paste the script under Site Header and click Save",
      },
      {
        title: "Verify",
        description: "Open your live Ghost site, then verify below",
      },
    ],
    shortSteps: [
      "Ghost Admin → Settings → Code Injection",
      "Paste the script in Site Header",
      "Save and verify",
    ],
    docsUrl: `${DOCS}/ghost-cms`,
  },
  {
    id: "kajabi",
    name: "Kajabi",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Site Details",
        description: "In Kajabi, go to Settings → Site Details",
      },
      {
        title: "Paste in Header Code",
        description: "Under Page Scripts, paste into Header Code and Save",
      },
      {
        title: "Verify",
        description: "Changes go live on save. Open your site and verify below",
      },
    ],
    shortSteps: [
      "Settings → Site Details → Page Scripts",
      "Paste into Header Code and Save",
      "Verify on your live Kajabi domain",
    ],
    docsUrl: `${DOCS}/kajabi`,
  },
  {
    id: "podia",
    name: "Podia",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Advanced settings",
        description: "In Podia, go to Settings → Advanced",
      },
      {
        title: "Paste in Header code",
        description: "Under Custom code → Header code, paste the script and Save changes",
      },
      {
        title: "Verify",
        description: "Script is live on save. Verify below",
      },
    ],
    shortSteps: [
      "Settings → Advanced → Custom code",
      "Paste into Header code and Save",
      "Verify on your Podia site",
    ],
    docsUrl: `${DOCS}/podia`,
  },
  {
    id: "lovable",
    name: "Lovable",
    setupTime: "~3 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open your Lovable project",
        description: "Find site settings or the global layout / index HTML",
      },
      {
        title: "Inject into <head>",
        description: "Paste the snippet so it appears in the <head> of every deployed page",
      },
      {
        title: "Publish and verify",
        description: "Publish/deploy on Lovable, then verify below",
      },
    ],
    shortSteps: [
      "Open your Lovable project settings or root layout",
      "Paste the script into the global <head>",
      "Publish and verify on the live URL",
    ],
    docsUrl: `${DOCS}/lovable`,
  },
  {
    id: "beehiiv",
    name: "Beehiiv",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Website settings",
        description: "In Beehiiv, go to Website → Settings (or Design → Custom code)",
      },
      {
        title: "Paste in Header / Custom code",
        description: "Add the script to the Header / head custom-code field for your website",
      },
      {
        title: "Save and verify",
        description: "Save, open your Beehiiv website URL, then verify below",
      },
    ],
    shortSteps: [
      "Beehiiv → Website → Settings / Custom code",
      "Paste the script in the Header field",
      "Save and verify on your Beehiiv site",
    ],
    docsUrl: DOCS,
  },
  {
    id: "kit",
    name: "Kit",
    setupTime: "~2 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open your landing page or Creator Profile",
        description: "In Kit (ConvertKit), edit the page or profile you want to track",
      },
      {
        title: "Add custom HTML / head code",
        description: "Paste the script into Custom HTML, Tracking, or Head code settings",
      },
      {
        title: "Publish and verify",
        description: "Publish the page, then verify on the live Kit URL",
      },
    ],
    shortSteps: [
      "Edit your Kit landing page or Creator Profile",
      "Paste the script into Custom HTML / Head code",
      "Publish and verify",
    ],
    docsUrl: DOCS,
  },
  {
    id: "bolt",
    name: "Bolt",
    setupTime: "~3 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Prompt Bolt to install",
        description:
          "In Bolt.new chat: ask it to add the Humblytics script to the <head> of all pages",
      },
      {
        title: "Deploy",
        description: "Deploy to Netlify/Vercel. Preview URLs won't verify",
      },
      {
        title: "Verify on live URL",
        description: "Open the deployed site, then verify below",
      },
    ],
    shortSteps: [
      "Ask Bolt to add the script to <head> on all pages",
      "Deploy to a live host (not Bolt preview)",
      "Verify on the deployed URL",
    ],
    docsUrl: `${DOCS}/bolt`,
  },
  {
    id: "astro",
    name: "Astro",
    setupTime: "~5 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open your layout",
        description: "Edit src/layouts/Layout.astro (or BaseLayout.astro)",
      },
      {
        title: "Add with is:inline",
        description:
          "Paste <script is:inline async src=\"…hmbl.min.js?id=…\"></script> before </head>",
      },
      {
        title: "Build and deploy",
        description: "Deploy production. Then verify (dev servers often won't verify)",
      },
    ],
    shortSteps: [
      "Open your Astro layout file",
      "Add the script with is:inline before </head>",
      "Build, deploy, and verify",
    ],
    docsUrl: `${DOCS}/astro`,
  },
  {
    id: "vuejs",
    name: "Vue.js",
    setupTime: "~5 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open index.html",
        description: "Add the script in public/index.html (or your app shell) before </head>",
      },
      {
        title: "SPA note",
        description: "For client-side routing, ensure the script loads once on the shell page",
      },
      {
        title: "Deploy and verify",
        description: "Deploy production, then verify below",
      },
    ],
    shortSteps: [
      "Add the script to index.html <head>",
      "Deploy your Vue app",
      "Verify on the production URL",
    ],
    docsUrl: `${DOCS}/vuejs`,
  },
  {
    id: "replit",
    name: "Replit",
    setupTime: "~3 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Edit your HTML shell",
        description: "Paste the script into the <head> of your Replit project's main HTML",
      },
      {
        title: "Publish / deploy",
        description: "Use Replit Deploy so the site is on a public .replit.app URL",
      },
      {
        title: "Verify",
        description: "Open the deployed URL and verify below",
      },
    ],
    shortSteps: [
      "Paste the script into your project <head>",
      "Deploy on Replit",
      "Verify on the public URL",
    ],
    docsUrl: `${DOCS}/replit`,
  },
  {
    id: "django",
    name: "Django",
    setupTime: "~5 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open base template",
        description: "Edit templates/base.html (or your shared layout)",
      },
      {
        title: "Paste before </head>",
        description: "Add the script in the base <head> so every page inherits it",
      },
      {
        title: "Deploy and verify",
        description: "Deploy, then verify on the production domain",
      },
    ],
    shortSteps: [
      "Add the script to templates/base.html <head>",
      "Deploy your Django app",
      "Verify on production",
    ],
    docsUrl: `${DOCS}/django`,
  },
  {
    id: "laravel",
    name: "Laravel",
    setupTime: "~5 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Open Blade layout",
        description: "Edit resources/views/layouts/app.blade.php (or your master layout)",
      },
      {
        title: "Paste before </head>",
        description: "Add the script inside the layout <head>",
      },
      {
        title: "Deploy and verify",
        description: "Deploy, then verify on the production domain",
      },
    ],
    shortSteps: [
      "Add the script to your Blade layout <head>",
      "Deploy your Laravel app",
      "Verify on production",
    ],
    docsUrl: `${DOCS}/laravel`,
  },
  {
    id: "reactrouter",
    name: "React Router",
    setupTime: "~5 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Add to the HTML shell",
        description: "Paste into index.html <head> (or root document) so it loads once",
      },
      {
        title: "SPA routing",
        description: "See the docs if you need route-change pageviews beyond the initial load",
      },
      {
        title: "Deploy and verify",
        description: "Deploy production, then verify below",
      },
    ],
    shortSteps: [
      "Add the script to index.html <head>",
      "Deploy your React Router app",
      "Verify on production",
    ],
    docsUrl: `${DOCS}/react-router`,
  },
  {
    id: "v0",
    name: "v0",
    setupTime: "~3 min",
    steps: [
      {
        title: "Copy the tracking code",
        description: "Use the Copy code button on your tracking snippet",
      },
      {
        title: "Ask v0 to install",
        description: "Prompt v0 to add the Humblytics script to the app <head> / root layout",
      },
      {
        title: "Deploy",
        description: "Deploy to Vercel (or your host). Preview-only URLs may not verify",
      },
      {
        title: "Verify",
        description: "Open the production URL and verify below",
      },
    ],
    shortSteps: [
      "Ask v0 to add the script to the root layout <head>",
      "Deploy to production",
      "Verify on the live URL",
    ],
    docsUrl: `${DOCS}/vercel-v0`,
  },
]

const PLATFORM_GUIDE_BY_ID: Record<PlatformId, PlatformGuide> = {
  ...Object.fromEntries(platformGuides.map((guide) => [guide.id, guide])),
  generic: GENERIC_GUIDE,
} as Record<PlatformId, PlatformGuide>

export function getPlatformGuide(platform: PlatformId): PlatformGuide {
  return PLATFORM_GUIDE_BY_ID[platform] ?? GENERIC_GUIDE
}

/** Platforms shown as selectable chips (excludes generic — handled as "Other"). */
const SELECTABLE_PLATFORMS: PlatformId[] = [
  ...PRIMARY_SELECTABLE_PLATFORMS,
  ...MORE_SELECTABLE_PLATFORMS,
]
